import React, { useState, useEffect } from 'react';
import { FaTimes, FaEdit, FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';
import QueryEditor from './QueryEditor';
import ThemedSelect from '../../../common/ThemedSelect';

const EditQueryModal = ({ query, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    queryName: '',
    queryDescription: '',
    sqlQuery: '',
    dataSourceId: 1,
    isPublic: false,
    executionTimeoutSeconds: 300,
    maxRows: 10000,
  });
  const [dataSources, setDataSources] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (query) {
      setFormData({
        queryName: query.query_name || '',
        queryDescription: query.query_description || '',
        sqlQuery: query.query_text || '',
        dataSourceId: query.data_source_id || 1,
        isPublic: query.is_public || false,
        executionTimeoutSeconds: query.execution_timeout_seconds || 300,
        maxRows: query.max_rows || 10000,
      });
    }
    const load = async () => {
      try {
        const res = await api.telegramBI.dataSources.getAll();
        setDataSources(res.data?.data || res.data || []);
      } catch {
        setDataSources([
          { data_source_id: 1, source_name: 'Main Database', source_type: 'sqlserver' },
        ]);
      }
    };
    load();
  }, [query]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleValidate = async (sql) => {
    const res = await api.telegramBI.queries.validate(sql, formData.dataSourceId);
    return res.data?.data || res.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.queryName.trim()) return toast.error('Query name is required');
    if (!formData.sqlQuery.trim()) return toast.error('SQL query is required');

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Details' },
    { id: 'query', label: 'SQL Query' },
    { id: 'settings', label: 'Settings' },
  ];

  const inputClass =
    'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-gray-900/60 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-surface rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <FaEdit className="text-blue-600 text-sm" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Edit Query</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex gap-0 px-6 border-b border-gray-100">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="px-6 py-5 overflow-y-auto flex-1">
            {activeTab === 'basic' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Name *</label>
                  <input
                    type="text"
                    name="queryName"
                    value={formData.queryName}
                    onChange={handleChange}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description
                  </label>
                  <textarea
                    name="queryDescription"
                    value={formData.queryDescription}
                    onChange={handleChange}
                    rows={3}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Data Source
                  </label>
                  <ThemedSelect
                    name="dataSourceId"
                    value={formData.dataSourceId}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    {dataSources.map((s) => (
                      <option key={s.data_source_id} value={s.data_source_id}>
                        {s.source_name} ({s.source_type})
                      </option>
                    ))}
                  </ThemedSelect>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isPublic"
                    checked={formData.isPublic}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Make this query public</span>
                </label>
              </div>
            )}

            {activeTab === 'query' && (
              <QueryEditor
                query={{ query_text: formData.sqlQuery }}
                onChange={(sql) => setFormData((prev) => ({ ...prev, sqlQuery: sql }))}
                onValidate={handleValidate}
              />
            )}

            {activeTab === 'settings' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Execution Timeout (seconds)
                  </label>
                  <input
                    type="number"
                    name="executionTimeoutSeconds"
                    value={formData.executionTimeoutSeconds}
                    onChange={handleChange}
                    min="30"
                    max="3600"
                    className={inputClass}
                  />
                  <p className="text-xs text-gray-400 mt-1">30–3600 seconds</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Maximum Rows
                  </label>
                  <input
                    type="number"
                    name="maxRows"
                    value={formData.maxRows}
                    onChange={handleChange}
                    min="100"
                    max="100000"
                    className={inputClass}
                  />
                  <p className="text-xs text-gray-400 mt-1">100–100,000 rows</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.queryName.trim() || !formData.sqlQuery.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {isSubmitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaCheck className="text-xs" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditQueryModal;
